<template>
  <teleport to="body">
    <div
      class="toast-viewport"
      aria-live="polite"
      aria-atomic="false"
      role="region"
      aria-label="Notifications"
    >
      <transition-group name="toast" tag="div" class="toast-list">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-item"
          :class="`toast-${toast.type}`"
          role="alert"
          @click="dismiss(toast.id)"
        >
          <!-- Icon -->
          <div class="toast-icon">
            <CheckCircle2Icon v-if="toast.type === 'success'" :size="18" />
            <XCircleIcon v-else-if="toast.type === 'error'" :size="18" />
            <AlertTriangleIcon
              v-else-if="toast.type === 'warning'"
              :size="18"
            />
            <InfoIcon v-else :size="18" />
          </div>

          <!-- Content -->
          <div class="toast-content">
            <p v-if="toast.title" class="toast-title">{{ toast.title }}</p>
            <p class="toast-message">{{ toast.message }}</p>
          </div>

          <!-- Close button -->
          <button
            class="toast-close"
            @click.stop="dismiss(toast.id)"
            aria-label="Dismiss notification"
          >
            <XIcon :size="14" />
          </button>

          <!-- Progress bar -->
          <div
            v-if="toast.duration > 0"
            class="toast-progress"
            :style="{ animationDuration: `${toast.duration}ms` }"
          />
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script>
import { computed } from "vue";
import { useToast } from "@/composables/useToast";
import {
  CheckCircle2 as CheckCircle2Icon,
  XCircle as XCircleIcon,
  AlertTriangle as AlertTriangleIcon,
  Info as InfoIcon,
  X as XIcon,
} from "lucide-vue-next";

export default {
  name: "AppToastContainer",
  components: {
    CheckCircle2Icon,
    XCircleIcon,
    AlertTriangleIcon,
    InfoIcon,
    XIcon,
  },
  setup() {
    const toast = useToast();
    const toasts = computed(() => toast.state.toasts);
    return {
      toasts,
      dismiss: (id) => toast.dismiss(id),
    };
  },
};
</script>

<style scoped>
/* ── Viewport (bottom-right stack) ── */
.toast-viewport {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
  pointer-events: none;
  max-width: 400px;
  width: calc(100vw - 48px);
}

.toast-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* ── Individual Toast ── */
.toast-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 12px;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.08);
  box-shadow: 0 10px 40px -8px rgba(15, 23, 42, 0.18),
    0 4px 16px -4px rgba(15, 23, 42, 0.08);
  cursor: pointer;
  pointer-events: all;
  position: relative;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
  min-width: 300px;
}

.toast-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 14px 48px -8px rgba(15, 23, 42, 0.22),
    0 6px 20px -4px rgba(15, 23, 42, 0.1);
}

/* ── Variants ── */
.toast-success {
  border-left: 3px solid #10b981;
}
.toast-error {
  border-left: 3px solid #ef4444;
}
.toast-warning {
  border-left: 3px solid #f59e0b;
}
.toast-info {
  border-left: 3px solid var(--info);
}

/* ── Icon ── */
.toast-icon {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toast-success .toast-icon {
  background: #f0fdf4;
  color: #10b981;
}
.toast-error .toast-icon {
  background: #fef2f2;
  color: #ef4444;
}
.toast-warning .toast-icon {
  background: #fffbeb;
  color: #f59e0b;
}
.toast-info .toast-icon {
  background: var(--info-bg);
  color: var(--info);
}

/* ── Content ── */
.toast-content {
  flex: 1;
  min-width: 0;
}

.toast-title {
  font-size: 0.8125rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 2px;
  line-height: 1.3;
}

.toast-message {
  font-size: 0.8125rem;
  color: #475569;
  margin: 0;
  line-height: 1.4;
  word-break: break-word;
}

/* ── Close button ── */
.toast-close {
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  padding: 2px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.15s, background 0.15s;
  margin-top: 1px;
}

.toast-close:hover {
  color: #475569;
  background: #f1f5f9;
}

/* ── Progress bar ── */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background: currentColor;
  animation: toast-shrink linear forwards;
  transform-origin: left;
  opacity: 0.35;
}

.toast-success .toast-progress {
  color: #10b981;
}
.toast-error .toast-progress {
  color: #ef4444;
}
.toast-warning .toast-progress {
  color: #f59e0b;
}
.toast-info .toast-progress {
  color: var(--info);
}

@keyframes toast-shrink {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* ── Transition animations ── */
.toast-enter-active {
  animation: toast-slide-in 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.toast-leave-active {
  animation: toast-slide-out 0.25s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

.toast-move {
  transition: transform 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

@keyframes toast-slide-in {
  from {
    opacity: 0;
    transform: translateX(20px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateX(0) scale(1);
  }
}

@keyframes toast-slide-out {
  from {
    opacity: 1;
    transform: translateX(0) scale(1);
    max-height: 200px;
  }
  to {
    opacity: 0;
    transform: translateX(20px) scale(0.96);
    max-height: 0;
    margin: 0;
    padding: 0;
  }
}
</style>
